/**
 * CreditsAdmin.jsx — FM5B Staff Credit Workspace
 * Authorization: FM5_IMPLEMENTATION_PLAN.md v2 §11.4
 *
 * Freeze gate: FM5 reason codes ONLY.
 * INV-FM5-CREDIT-01: FM4A/FM4C reason codes are PROHIBITED here.
 * INV-FM5-CREDIT-02: amount must be > 0.
 * INV-FM4A-CREDIT: APPLIED → REVERSED only (frozen lifecycle, respected).
 *
 * Tabs:
 *  1. All Credits  — full FM5 credit list (filter by status/reason)
 *  2. Pending      — DRAFT awaiting approval
 *  3. History      — APPLIED and REVERSED
 */

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { RefreshCw, Plus, XCircle, Shield, CheckCircle, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const t = localStorage.getItem("academiaos_token");
  return { Authorization: `Bearer ${t}`, "Content-Type": "application/json" };
}

const fmt = (v) => {
  const n = parseFloat(v || 0);
  return isNaN(n) ? "₹0.00" : `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

// FM5-owned reason codes ONLY (INV-FM5-CREDIT-01)
const FM5_REASON_CODES = [
  { value: "OVERPAYMENT_CREDIT",        label: "Overpayment Credit" },
  { value: "FEE_ADJUSTMENT",            label: "Fee Adjustment" },
  { value: "PROGRAM_WITHDRAWAL_CREDIT", label: "Program Withdrawal Credit" },
  { value: "CANCELLATION_CREDIT",       label: "Cancellation Credit" },
];

const STATUS_COLORS = {
  DRAFT:    "bg-slate-50 text-slate-600 border-slate-200",
  APPROVED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  APPLIED:  "bg-blue-50 text-blue-700 border-blue-200",
  REVERSED: "bg-rose-50 text-rose-600 border-rose-200",
};

function StatusBadge({ status }) {
  const cls = STATUS_COLORS[status] || "bg-slate-50 text-slate-600 border-slate-200";
  return <span className={`px-2 py-0.5 text-xs font-medium border rounded ${cls}`}>{status}</span>;
}

// ── Create Credit Modal ───────────────────────────────────────────────────────
function CreateCreditModal({ institutionId, onClose, onDone }) {
  const [form, setForm] = useState({
    student_account_id: "", charge_id: "",
    reason_code: "OVERPAYMENT_CREDIT", amount: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.student_account_id || !form.charge_id || !form.amount) {
      toast.error("Student account, charge ID, and amount are required"); return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/credits?institution_id=${institutionId}`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create credit");
      toast.success("Credit memo created (DRAFT)");
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="create-credit-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Create FM5 Credit Memo</h3>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-blue-50 border border-blue-100 rounded p-2 text-xs text-blue-800">
            <Shield className="w-3 h-3 inline mr-1" />
            <strong>INV-FM5-CREDIT-01:</strong> Only FM5 reason codes permitted here.
            Scholarship, waiver, and reimbursement codes are FM4A/FM4C-owned and blocked.
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Student Account ID *</label>
            <input data-testid="credit-student-account-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.student_account_id} onChange={e => setForm(f => ({...f, student_account_id: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Charge ID *</label>
            <input data-testid="credit-charge-id"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              placeholder="UUID of the ACTIVE charge"
              value={form.charge_id} onChange={e => setForm(f => ({...f, charge_id: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Reason Code *</label>
            <select data-testid="credit-reason-code"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.reason_code} onChange={e => setForm(f => ({...f, reason_code: e.target.value}))}>
              {FM5_REASON_CODES.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Amount (INR) * (must be &gt; 0 and ≤ charge amount)</label>
            <input data-testid="credit-amount" type="number" min="0.01"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.amount} onChange={e => setForm(f => ({...f, amount: e.target.value}))} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes (optional)</label>
            <textarea rows={2} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
              value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} />
          </div>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="create-credit-submit" onClick={submit} disabled={saving}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Creating…" : "Create Credit (DRAFT)"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Reverse Modal ─────────────────────────────────────────────────────────────
function ReverseModal({ cm, institutionId, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const reverse = async () => {
    if (!reason.trim()) { toast.error("Reversal reason is required (INV-FM4A-CREDIT)"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/credits/${cm.id}/reverse?institution_id=${institutionId}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ reversal_reason: reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reversal failed");
      toast.success(`Credit reversed. ${data.invariant_note}`);
      onDone(); onClose();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
        <div className="px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">Reverse Credit Memo</h3>
        </div>
        <div className="p-5 space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
            Reversing restores the student's original liability. (INV-FM4A-BALANCE)
          </div>
          <textarea data-testid="reverse-reason-input" rows={3} placeholder="Reversal reason (required)"
            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
            value={reason} onChange={e => setReason(e.target.value)} />
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">Cancel</button>
          <button data-testid="reverse-credit-confirm" onClick={reverse} disabled={saving}
            className="px-3 py-1.5 text-sm bg-rose-600 text-white rounded-lg disabled:opacity-50">
            {saving ? "Reversing…" : "Reverse Credit"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Credit Row ────────────────────────────────────────────────────────────────
function CreditRow({ cm, institutionId, canIssue, onRefresh }) {
  const [modal, setModal] = useState(null);

  const transition = async (action) => {
    try {
      const res = await fetch(`${API}/api/finance/fm5/credits/${cm.id}/${action}?institution_id=${institutionId}`,
        { method: "PATCH", headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `${action} failed`);
      const notes = data.invariant_note ? ` — ${data.invariant_note}` : "";
      toast.success(`Credit ${action}d${notes}`);
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <>
      <tr className="border-b border-slate-100 hover:bg-slate-50 text-sm" data-testid={`credit-row-${cm.id}`}>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">{cm.id.slice(0,8)}…</td>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">{cm.student_account_id?.slice(0,8)}…</td>
        <td className="px-4 py-3"><StatusBadge status={cm.status} /></td>
        <td className="px-4 py-3 text-right font-medium">{fmt(cm.amount)}</td>
        <td className="px-4 py-3 text-xs text-slate-600">{cm.reason_code?.replace(/_/g," ")}</td>
        <td className="px-4 py-3 text-xs text-slate-400">{cm.created_at ? new Date(cm.created_at).toLocaleDateString("en-IN") : "—"}</td>
        <td className="px-4 py-3">
          {canIssue && (
            <div className="flex gap-1 flex-wrap">
              {cm.status === "DRAFT" && (
                <button onClick={() => transition("approve")} data-testid={`credit-approve-btn-${cm.id}`}
                  className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-100">Approve</button>
              )}
              {cm.status === "APPROVED" && (
                <button onClick={() => transition("apply")} data-testid={`credit-apply-btn-${cm.id}`}
                  className="px-2 py-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100">Apply</button>
              )}
              {cm.status === "APPLIED" && (
                <button onClick={() => setModal("reverse")} data-testid={`credit-reverse-btn-${cm.id}`}
                  className="px-2 py-1 text-xs bg-rose-50 text-rose-600 border border-rose-200 rounded hover:bg-rose-100">
                  <RotateCcw className="w-3 h-3 inline mr-1" />Reverse
                </button>
              )}
            </div>
          )}
        </td>
      </tr>
      {modal === "reverse" && (
        <tr><td colSpan={7}>
          <ReverseModal cm={cm} institutionId={institutionId} onClose={() => setModal(null)} onDone={onRefresh} />
        </td></tr>
      )}
    </>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function CreditsAdmin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("all");
  const [credits, setCredits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const institutionId = user?.institution_id;
  const role = user?.role;
  const canIssue = ["finance_officer","finance_manager","cfo","institution_admin","super_admin"].includes(role);

  const load = useCallback(async () => {
    if (!institutionId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/finance/fm5/credits?institution_id=${institutionId}`, { headers: authHeaders() });
      const data = await res.json();
      setCredits(Array.isArray(data) ? data : []);
    } catch { toast.error("Failed to load credits"); }
    finally { setLoading(false); }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const displayed = (() => {
    if (tab === "pending")  return credits.filter(c => c.status === "DRAFT");
    if (tab === "history")  return credits.filter(c => ["APPLIED","REVERSED"].includes(c.status));
    return credits;
  })();

  const tabs = [
    { id: "all",     label: "All FM5 Credits", count: credits.length },
    { id: "pending", label: "Pending Approval", count: credits.filter(c=>c.status==="DRAFT").length },
    { id: "history", label: "History" },
  ];

  return (
    <div className="p-6 space-y-5" data-testid="credits-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Credits</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            FM5B — Credit memo lifecycle. FM5 reason codes only — FM4A/FM4C codes are blocked.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
          </button>
          {canIssue && (
            <button onClick={() => setShowCreate(true)} data-testid="create-credit-btn"
              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              <Plus className="w-4 h-4" /> Create Credit
            </button>
          )}
        </div>
      </div>

      <div className="flex border-b border-slate-200 gap-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`credits-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}>
            {t.label}
            {t.count > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-slate-100 text-slate-600 rounded-full">{t.count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading…</div>
      ) : displayed.length === 0 ? (
        <div className="text-center py-12 text-slate-400" data-testid="credits-empty">
          <CheckCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No FM5 credit memos{tab !== "all" ? ` in ${tab}` : ""}</p>
          <p className="text-xs mt-1">FM5 credits use: {FM5_REASON_CODES.map(r => r.label).join(", ")}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full text-left" data-testid="credits-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Student Account</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Reason Code (FM5)</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(cm => (
                <CreditRow key={cm.id} cm={cm} institutionId={institutionId} canIssue={canIssue} onRefresh={load} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCreditModal institutionId={institutionId} onClose={() => setShowCreate(false)} onDone={load} />
      )}
    </div>
  );
}
